import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { ResponsivePie } from '@nivo/pie'

const SCORE_COLORS = {
  0: '#d32f2f',
  1: '#e53935',
  2: '#e64a19',
  3: '#f57c00',
  4: '#ffa000',
  5: '#fdd835',
  6: '#c0ca33',
  7: '#7cb342',
  8: '#43a047',
  9: '#2e7d32',
  10: '#1b5e20',
}

const MIN_VISUAL_PERCENT = 2.5
const ELBOW_OFFSET = 28
const H_LEN = 24
const MIN_SPACING = 16
const LINK_FONT_SIZE = 11

// Fixed angles (upper-right, upper-left) used when there are too few slices
// to trust arc midpoints — avoids labels appearing below the chart.
const FEW_SLICE_ANGLES = [-Math.PI / 4, Math.PI + Math.PI / 4]

const AllSliceLabels = ({ dataWithArc, centerX, centerY, radius }) => {
  const filtered = dataWithArc.filter((d) => d.arc.endAngle - d.arc.startAngle > 0.001)
  const useForcedAngles = filtered.length <= 2

  const items = filtered.map((d, i) => {
      const mid = useForcedAngles
        ? FEW_SLICE_ANGLES[i] ?? -Math.PI / 4
        : (d.arc.startAngle + d.arc.endAngle) / 2
      const sin = Math.sin(mid)
      const cos = Math.cos(mid)
      return {
        id: d.id,
        color: d.color,
        label: `${d.id}: ${d.data.percent}%`,
        outerX: sin * radius + centerX,
        outerY: -cos * radius + centerY,
        elbowX: sin * (radius + ELBOW_OFFSET) + centerX,
        naturalY: -cos * (radius + ELBOW_OFFSET) + centerY,
        isRight: sin >= 0,
      }
    })

  const stack = (group) =>
    [...group]
      .sort((a, b) => a.naturalY - b.naturalY)
      .reduce((acc, l, i) => {
        const labelY =
          i === 0 ? l.naturalY : Math.max(l.naturalY, acc[i - 1].labelY + MIN_SPACING)
        return [...acc, { ...l, labelY }]
      }, [])

  const allLabels = [
    ...stack(items.filter((l) => l.isRight)),
    ...stack(items.filter((l) => !l.isRight)),
  ]

  return (
    <g>
      {allLabels.map((l) => {
        const lineEndX = l.isRight ? l.elbowX + H_LEN : l.elbowX - H_LEN
        const textX = l.isRight ? lineEndX + 3 : lineEndX - 3
        return (
          <g key={l.id}>
            <polyline
              points={`${l.outerX},${l.outerY} ${l.elbowX},${l.labelY} ${lineEndX},${l.labelY}`}
              fill='none'
              stroke={l.color}
              strokeWidth={1.5}
            />
            <text
              x={textX}
              y={l.labelY}
              textAnchor={l.isRight ? 'start' : 'end'}
              dominantBaseline='middle'
              style={{ fontSize: LINK_FONT_SIZE, fill: '#333' }}
            >
              {l.label}
            </text>
          </g>
        )
      })}
    </g>
  )
}

AllSliceLabels.propTypes = {
  dataWithArc: PropTypes.array.isRequired,
  centerX: PropTypes.number.isRequired,
  centerY: PropTypes.number.isRequired,
  radius: PropTypes.number.isRequired,
}

const SatisfactionSurveyChart = ({ data }) => {
  const chartData = useMemo(() => {
    const total = data.reduce((sum, item) => sum + (item.count || 0), 0)
    if (total === 0) return []

    const minDisplayValue = (MIN_VISUAL_PERCENT / 100) * total

    return data
      .filter((item) => item.count > 0)
      .map((item) => ({
        id: String(item.rating),
        label: String(item.rating),
        value: Math.max(item.count, minDisplayValue),
        realCount: item.count,
        percent: ((item.count / total) * 100).toFixed(1),
        color: SCORE_COLORS[item.rating] ?? '#999',
      }))
  }, [data])

  if (chartData.length === 0) {
    return null
  }

  const legendData = [...chartData].sort((a, b) => b.value - a.value)

  return (
    <div className='satisfaction-survey-chart-wrapper'>
      <div className='satisfaction-survey-chart-pie'>
        <ResponsivePie
          data={chartData}
          colors={{ datum: 'data.color' }}
          margin={{ top: 60, right: 100, bottom: 60, left: 100 }}
          innerRadius={0}
          padAngle={0.5}
          cornerRadius={2}
          sortByValue
          enableArcLabels={false}
          enableArcLinkLabels={false}
          layers={['arcs', AllSliceLabels]}
          borderWidth={0.5}
          borderColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
          motionConfig='gentle'
          tooltip={({ datum }) => (
            <div className='satisfaction-survey-chart-tooltip'>
              <span
                className='satisfaction-survey-chart-tooltip-dot'
                style={{ backgroundColor: datum.color }}
              />
              <span>
                <strong>{datum.id}</strong>: {datum.data.percent}% ({datum.data.realCount})
              </span>
            </div>
          )}
        />
      </div>

      <div className='satisfaction-survey-chart-legend'>
        {legendData.map((item) => (
          <div key={item.id} className='satisfaction-survey-chart-legend-item'>
            <span
              className='satisfaction-survey-chart-legend-dot'
              style={{ backgroundColor: item.color }}
            />
            <span className='satisfaction-survey-chart-legend-label'>
              {item.id}: {item.percent}%
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

SatisfactionSurveyChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      rating: PropTypes.number.isRequired,
      count: PropTypes.number.isRequired,
    })
  ).isRequired,
}

export default SatisfactionSurveyChart
